import { ErrorOutlineOutlined } from '@mui/icons-material'
import { Box, Stack, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'
import { useTranslation } from 'react-i18next'

import CustomButton from '@/components/inputs/CustomButton'

type LocalPqResourceSetsErrorStateProps = {
  onRetry: () => void
}

/**
 * Shown in place of the rows when the list fetch fails. Distinct from the
 * first-run empty state: it must never read as "you have no resource sets" (which
 * would nudge the user to create duplicates), so it names the failure and offers
 * a retry. A global alert is also raised by the query's error handler.
 */
function LocalPqResourceSetsErrorState({ onRetry }: Readonly<LocalPqResourceSetsErrorStateProps>) {
  const { t } = useTranslation()

  return (
    <Stack
      sx={{
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        gap: 2,
        py: 8,
        px: 3,
        height: '100%',
      }}
    >
      <Box
        sx={{
          display: 'grid',
          placeItems: 'center',
          width: 64,
          height: 64,
          borderRadius: '50%',
          color: 'error.main',
          backgroundColor: (theme) => alpha(theme.palette.error.main, 0.1),
        }}
      >
        <ErrorOutlineOutlined fontSize="large" />
      </Box>
      <Stack sx={{ gap: 1, maxWidth: 460 }}>
        <Typography variant="h6">{t('prequalifications.list.error.title')}</Typography>
        <Typography color="text.secondary" variant="body2">
          {t('prequalifications.list.error.body')}
        </Typography>
      </Stack>
      <CustomButton color="primary" variant="outlined" onClick={onRetry}>
        {t('prequalifications.list.error.retry')}
      </CustomButton>
    </Stack>
  )
}

export default LocalPqResourceSetsErrorState
